import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { KeyRound, LoaderCircle, Lock, Mail } from 'lucide-react'
import type { LoginOptions, SsoProvider } from '@takeit4me/shared'
import { useAuth } from '../auth'
import { publicApi } from '../api'
import { subdomainCompany } from '../lib/company'
import { useDebounced } from '../lib/hooks'
import { AuthShell, FormError, FormNote, PROVIDER_LABELS } from './auth/AuthShell'

type Mode = 'password' | 'link' | 'reset'

/**
 * Staff sign-in (design 16.3, 17.1): single sign-on where the company uses it, a password, or a one-time
 * link by email; and a password reset. The company comes from the subdomain, else from the email's domain.
 */
export function LoginPage() {
  const auth = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mode, setMode] = useState<Mode>('password')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const opts = useLoginOptions(email)
  const sso = opts?.sso_providers ?? []
  const passwordAllowed = opts?.password_allowed ?? true

  const run = async (fn: () => Promise<void>, done?: string) => {
    setBusy(true); setError(null); setNote(null)
    try { await fn(); if (done) setNote(done) } catch (err) { setError(err instanceof Error ? err.message : String(err)) } finally { setBusy(false) }
  }
  const provider = (p: SsoProvider) => run(() => (p === 'saml' ? auth.signInWithSaml(opts?.saml_domain ?? email.split('@')[1] ?? '') : auth.signInWithProvider(p)))

  return (
    <AuthShell title="takeit4me" logo={opts?.company?.logo_url ?? null} sub={opts?.company ? `Sign in to ${opts.company.name}` : "Sign in to your team's console"}>
      {sso.length > 0 && (
        <div className="flex flex-col gap-2 mb-4">
          {sso.map((p) => (
            <button key={p} type="button" className="btn w-full h-10 justify-center" disabled={busy || (p === 'saml' && !opts?.saml_domain && !email.includes('@'))} onClick={() => void provider(p)}>
              <KeyRound /> {PROVIDER_LABELS[p]}
            </button>
          ))}
          {passwordAllowed && <div className="flex items-center gap-2 text-faint text-[11px] my-1"><span className="flex-1 border-t border-line" />or<span className="flex-1 border-t border-line" /></div>}
        </div>
      )}

      {!passwordAllowed ? (
        <div className="text-muted text-[12.5px] mb-2">{opts?.company?.name ?? 'Your company'} signs in with single sign-on only. Use the button above.</div>
      ) : (
        <form onSubmit={(e) => {
          e.preventDefault()
          if (mode === 'password') void run(() => auth.signIn(email.trim(), password))
          else if (mode === 'link') void run(() => auth.sendMagicLink(email.trim()), `We've emailed a sign-in link to ${email.trim()}. It works once and expires in an hour.`)
          else void run(() => auth.sendPasswordReset(email.trim()), `If ${email.trim()} has an account, we've emailed a link to choose a new password.`)
        }}>
          <label className="label" htmlFor="email">Work email</label>
          <div className="relative mb-3">
            <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-faint" />
            <input id="email" className="input w-full pl-9 h-10" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.example" />
          </div>
          {mode === 'password' && (
            <>
              <div className="flex items-center"><label className="label" htmlFor="pw">Password</label>
                <button type="button" className="ml-auto text-[11.5px] text-brand font-semibold mb-1" onClick={() => { setMode('reset'); setError(null); setNote(null) }}>Forgot password?</button>
              </div>
              <div className="relative mb-4">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-faint" />
                <input id="pw" className="input w-full pl-9 h-10" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
            </>
          )}
          {mode === 'reset' && <div className="text-muted text-[12px] mb-3">We'll email you a link to choose a new password.</div>}
          {mode === 'link' && <div className="text-muted text-[12px] mb-3">We'll email you a one-time link. No password needed.</div>}
          <FormError>{error}</FormError>
          <FormNote>{note}</FormNote>
          <button className="btn pri w-full h-10 text-[13px]" disabled={busy}>
            {busy && <LoaderCircle className="spin" />} {mode === 'password' ? 'Sign in' : mode === 'link' ? 'Email me a sign-in link' : 'Email me a reset link'}
          </button>
          <div className="flex justify-center gap-3 mt-3 text-[12px]">
            {mode !== 'password' && <button type="button" className="text-brand font-semibold" onClick={() => { setMode('password'); setError(null); setNote(null) }}>Use my password</button>}
            {mode !== 'link' && <button type="button" className="text-brand font-semibold" onClick={() => { setMode('link'); setError(null); setNote(null) }}>Email me a sign-in link</button>}
          </div>
        </form>
      )}
      <div className="text-faint text-[11.5px] mt-5 text-center">
        Your data stays in your company's region. New to takeit4me? <Link to="/signup" className="text-brand font-semibold">Create an account</Link>
      </div>
    </AuthShell>
  )
}

/** Which sign-in methods to offer: from the subdomain's company, or the typed email's domain. Fails quietly to password only. */
export function useLoginOptions(email: string): LoginOptions | null {
  const [opts, setOpts] = useState<LoginOptions | null>(null)
  const company = subdomainCompany()
  const domainEmail = useDebounced(email.includes('@') && /\.[a-z]{2,}$/i.test(email) ? email.trim().toLowerCase() : '', 400)
  useEffect(() => {
    if (!company && !domainEmail) { setOpts(null); return }
    let live = true
    publicApi.loginOptions(company ? { company } : { email: domainEmail }).then((o) => { if (live) setOpts(o) }, () => { if (live) setOpts(null) })
    return () => { live = false }
  }, [company, domainEmail])
  return opts
}
